import assignmentService from 'ApiService/Teacher/Assignment';
import { AuthContext } from 'contexts/AuthContext';
import Modal from 'pages/Student/Assignment/UploadAssignment/Steps/Model';
import React, { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';

const StudentUploadAssignment = ({ selectedAssignment }) => {
  const [getFiles, setGetFiles] = useState([]);
  const { state } = useContext(AuthContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleFileClick = (file) => {
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await assignmentService.getuploadedAssignments(
        selectedAssignment._id
      );
      if (response.data.success) {
        setGetFiles(response.data.data || []);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch attended tests.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [state.user, selectedAssignment._id]);

  return (
    <div>
      <div className="mt-3 h-full w-full">
        <div className="md:2/3 mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <BallTriangle color="#5431ed" />
            </div>
          ) : getFiles?.length === 0 ? (
            <p className="col-span-full text-center text-gray-600">
              No files available
            </p>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {getFiles.map((data, index) => {
                if (!data.filename) {
                  return (
                    <div
                      key={index}
                      className="col-span-full text-center text-gray-600"
                    >
                      Missing filename for file.
                    </div>
                  );
                }
                const fileExtension = data.filename
                  .split('.')
                  .pop()
                  .toLowerCase();
                const filePath = `${BASE_URL}/${data.filepath}`;

                // Define common display details block
                const details = (
                  <div className="mt-2 text-sm text-gray-700">
                    <p>
                      <strong>Student:</strong>{' '}
                      {data.student?.name || 'Unknown'}
                    </p>
                    <p>
                      <strong>Submitted On:</strong>{' '}
                      {new Date(data.submittedOn).toLocaleString()}
                    </p>
                  </div>
                );

                const commonClasses =
                  'h-40 w-full rounded-md transition hover:opacity-75 object-cover';

                if (['png', 'jpeg', 'jpg'].includes(fileExtension)) {
                  return (
                    <div key={index} className="relative cursor-pointer">
                      <img
                        src={filePath}
                        alt={data.filename}
                        className={commonClasses}
                        onClick={() =>
                          handleFileClick({
                            type: 'image',
                            src: filePath,
                            filename: data.filename,
                          })
                        }
                      />
                      {details}
                    </div>
                  );
                } else if (['pdf'].includes(fileExtension)) {
                  return (
                    <div key={index} className="relative cursor-pointer">
                      <div className={commonClasses + ' object-contain'}>
                        <embed
                          src={filePath}
                          type="application/pdf"
                          className="h-full w-full"
                          onClick={() =>
                            handleFileClick({
                              type: 'pdf',
                              src: filePath,
                            })
                          }
                        />
                      </div>
                      {details}
                    </div>
                  );
                } else if (
                  ['mp4', 'mkv', 'avi', 'mov'].includes(fileExtension)
                ) {
                  return (
                    <div key={index} className="relative cursor-pointer">
                      <video
                        controls
                        className={commonClasses + ' object-contain'}
                        onClick={() =>
                          handleFileClick({
                            type: 'video',
                            src: filePath,
                          })
                        }
                      >
                        <source
                          src={filePath}
                          type={`video/${fileExtension}`}
                        />
                        Your browser does not support the video tag.
                      </video>
                      {details}
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={index}
                      className="col-span-full text-center text-gray-600"
                    >
                      Unsupported file type: {data.filename}
                    </div>
                  );
                }
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal Component */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        file={selectedFile}
        fetchData={fetchData}
      />
    </div>
  );
};

export default StudentUploadAssignment;
